import React, { memo, useState } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { LayoutGrid, LoadingContainer } from '@redlink/components/atoms';
import { ChartColorGuid, usePlan } from '@redlink/components/molecules';
import {
  VictoryLine,
  VictoryChart,
  VictoryTheme,
  VictoryAxis,
  VictoryTooltip,
} from 'victory-native';
import { VictoryVoronoiContainer } from 'victory';
import { useLocale } from '@redlink/assets/locale';
import { useMoneyMask } from '@redlink/core';
import { BodyBlockContainer } from './bodyBlockContainer';
import { ReportSummaryList } from './reportSummaryList';
import { useQuery, getPageStatsWithChartQuery } from '@redlink/services';

export const ViewBlock = memo(({ isProUser }) => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { getValue } = useMoneyMask();
  const [periodType, setPeriodType] = useState(
    isProUser ? 'lastMonth' : 'last7Days'
  );
  const { showPlanDialog } = usePlan();

  const {
    loading,
    data: {
      PageStatsWithChart: {
        viewCount = 0,
        clickCount = 0,
        pageStats = [],
        linksStats = [],
      } = {},
    },
  } = useQuery(getPageStatsWithChartQuery, {
    variables: {
      period: periodType,
    },
    limited: () => {
      showPlanDialog();
    },
  });

  const data = [
    {
      title: dictionary.viewCount,
      value: viewCount,
      valueFormatter: (value) => getValue(value),
    },
    {
      title: dictionary.clickCount,
      value: clickCount,
      valueFormatter: (value) => getValue(value),
    },
  ];

  return (
    <LoadingContainer isBusy={loading}>
      <LayoutGrid>
        <BodyBlockContainer
          isProUser={isProUser}
          title={dictionary.pageViewStat}
          selectedPeriodTypeKey={periodType}
          onChangePeriodTypeKey={(i) => setPeriodType(i)}
        >
          <ReportSummaryList {...{ data }} />
        </BodyBlockContainer>
        <ViewChart {...{ pageStats, linksStats }} />
      </LayoutGrid>
    </LoadingContainer>
  );
});

const ViewChart = memo(({ pageStats, linksStats }) => {
  const { styles, colors } = useStyle();
  const [width, setWidth] = useState(0);
  const { dictionary } = useLocale();

  const chartColorGuidData = [
    {
      color: colors.yellow2,
      title: dictionary.viewColor,
    },
    {
      color: colors.blue1,
      title: dictionary.clickColor,
    },
  ];

  return (
    <View
      onLayout={({
        nativeEvent: {
          layout: { width },
        },
      }) => setWidth(width)}
    >
      <ChartColorGuid
        data={chartColorGuidData}
        style={styles.colorGuidContainer}
      />
      <VictoryChart
        height={300}
        width={width - 40}
        padding={{ left: 20, right: 20, top: 15, bottom: 60 }}
        theme={VictoryTheme.material}
        containerComponent={<VictoryVoronoiContainer voronoiDimension="x" />}
      >
        <VictoryAxis
          fixLabelOverlap
          style={{
            tickLabels: { fill: colors.gray22 },
            ticks: { stroke: colors.gray22 },
            grid: { stroke: colors.gray7 },
          }}
        />
        <VictoryAxis
          dependentAxis
          tickFormat={(y) => (y % 1 === 0 ? y : '')}
          style={{
            tickLabels: { fill: colors.gray22 },
            ticks: { stroke: colors.gray22 },
            grid: { stroke: colors.gray7 },
          }}
        />
        <VictoryLine
          style={{ data: { stroke: colors.blue1, strokeWidth: 4 } }}
          data={linksStats.map(({ label: x, value: y }) => ({ x, y }))}
          labels={({ datum }) => datum.y}
          labelComponent={<VictoryTooltip orientation="bottom" />}
        />
        <VictoryLine
          style={{ data: { stroke: colors.yellow2, strokeWidth: 4 } }}
          data={pageStats.map(({ label: x, value: y }) => ({ x, y }))}
          labels={({ datum }) => datum.y}
          labelComponent={<VictoryTooltip orientation="top" />}
        />
      </VictoryChart>
    </View>
  );
});

const useStyle = createUseStyle(({ colors }) => ({
  colorGuidContainer: {
    paddingHorizontal: 15,
  },
}));
